<template>
  <div class="my-6">
    <!-- Header -->
    <div class="mb-10">
      <h2 class="mt-4 text-3xl font-bold text-[#34495e]">
        Payment Installment
      </h2>

      <p class="mt-2 max-w-2xl leading-7 text-zinc-500">
        Choose how you would like to pay your annual premium. Select an
        installment option that best fits your budget before proceeding to
        payment.
      </p>
    </div>

    <div class="bg-white">
      <!-- Options -->
      <div class="grid gap-6 p-6 md:grid-cols-2 xl:grid-cols-3">
        <label
          v-for="option in installmentOptions"
          :key="option.id"
          :class="[
            'group relative cursor-pointer overflow-hidden rounded-2xl border bg-white p-6 transition-all duration-300',
            selectedInstallment === option.id
              ? 'border-orange-500 ring-2 ring-orange-100 shadow-lg'
              : 'border-zinc-200 hover:-translate-y-1 hover:border-orange-300 hover:shadow-md',
          ]"
        >
          <input
            v-model="selectedInstallment"
            :value="option.id"
            type="radio"
            class="hidden"
          />

          <!-- Selected Badge -->
          <div
            v-if="selectedInstallment === option.id"
            class="absolute right-4 top-4 rounded-full bg-orange-500 px-3 py-1 text-xs font-semibold text-white"
          >
            Selected
          </div>

          <!-- Recommended -->
          <div
            v-else-if="option.recommended"
            class="absolute right-4 top-4 rounded-full bg-green-100 px-3 py-1 text-xs font-semibold text-green-700"
          >
            Recommended
          </div>

          <!-- Plan -->
          <div class="mt-2">
            <h4 class="text-xl font-bold text-[#34495e]">
              {{ option.title }}
            </h4>

            <p class="mt-2 text-sm leading-6 text-zinc-500">
              {{ option.description }}
            </p>
          </div>

          <!-- Amount -->
          <div class="mt-8">
            <p class="text-xs uppercase tracking-wide text-zinc-400">
              Amount Due Today
            </p>

            <h2 class="mt-2 text-4xl font-extrabold text-orange-600">
              {{ amountPaymentChannelFormat(option.amountToPay) }}
            </h2>

            <p class="mt-1 text-sm text-zinc-500">
              {{ option.term }}
            </p>
          </div>

          <!-- Divider -->
          <div class="my-6 border-t border-dashed border-zinc-200"></div>

          <!-- Details -->
          <div class="space-y-3">
            <div class="flex items-center justify-between">
              <span class="text-sm text-zinc-500"> Total Premium </span>

              <span class="font-semibold text-[#34495e]">
                {{ amountPaymentChannelFormat(option.totalPremium) }}
              </span>
            </div>

            <div class="flex items-center justify-between">
              <span class="text-sm text-zinc-500"> Installments </span>

              <span
                class="rounded-full bg-zinc-100 px-2.5 py-1 text-xs font-semibold text-zinc-700"
              >
                {{ option.title }}
              </span>
            </div>
          </div>
        </label>
      </div>

      <div class="rounded-2xl bg-white">
        <!-- Options -->
        <div class="grid gap-6 p-6 md:grid-cols-2 xl:grid-cols-3">
          <!-- Your existing v-for cards -->
        </div>

        <!-- Checkout Footer -->
        <div class="border-t border-zinc-200 bg-zinc-50 px-6 py-5">
          <div
            class="flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between"
          >
            <!-- Selected Summary -->
            <div>
              <p class="text-sm text-zinc-500">Selected Payment Option</p>

              <h3 class="mt-1 text-lg font-semibold text-[#34495e]">
                {{ selectedOption.title }}
              </h3>

              <div class="mt-3 flex items-center gap-6">
                <div>
                  <p class="text-xs uppercase tracking-wide text-zinc-400">
                    Amount Due Today
                  </p>

                  <p class="text-3xl font-bold text-orange-600">
                    {{ amountPaymentChannelFormat(selectedOption.amountToPay) }}
                  </p>
                </div>

                <div
                  class="hidden h-10 border-l border-zinc-300 lg:block"
                ></div>

                <div>
                  <p class="text-xs uppercase tracking-wide text-zinc-400">
                    Total Premium
                  </p>

                  <p class="text-lg font-semibold text-[#34495e]">
                    {{
                      amountPaymentChannelFormat(selectedOption.totalPremium)
                    }}
                  </p>
                </div>
              </div>
            </div>

            <!-- Proceed -->
            <button
              @click="submitCheckout()"
              class="w-full rounded-xl bg-orange-500 px-8 py-4 font-semibold text-white transition-all duration-200 hover:bg-orange-600 hover:shadow-lg lg:w-auto"
            >
              Proceed to Checkout
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import reusableBase from "@/utils/composables/format";
import { ref, onMounted, inject, computed } from "vue";
import { useRouter } from "vue-router";
export default {
  setup() {
    const router = useRouter();
    const { amountPaymentChannelFormat } = reusableBase();
    const store = inject("$store");
    const payableAmount = ref();
    onMounted(() => {
      payableAmount.value = store.state.planDetails.premium;
      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    });
    const selectedInstallment = ref(1);

    const installmentOptions = ref([
      {
        id: 1,
        title: "3 Months",
        description: "Split your premium into 3 equal monthly payments.",
        amountToPay: store.state.planDetails.premium / 3,
        totalPremium: store.state.planDetails.premium,
        term: "3 Monthly Payments",
        recommended: true,
      },
      {
        id: 2,
        title: "4 Months",
        description: "Pay your premium over 4 months.",
        amountToPay: store.state.planDetails.premium / 4,
        totalPremium: store.state.planDetails.premium,
        term: "4 Monthly Payments",
        recommended: false,
      },
      {
        id: 3,
        title: "6 Months",
        description: "Lower monthly payments across 6 months.",
        amountToPay: store.state.planDetails.premium / 6,
        totalPremium: store.state.planDetails.premium,
        term: "6 Monthly Payments",
        recommended: false,
      },
    ]);
    const selectedOption = computed(() =>
      installmentOptions.value.find(
        (item) => item.id === selectedInstallment.value
      )
    );
    const submitCheckout = () => {
      store.commit("SET_SELECTED_INSTALLMENT", selectedOption.value);

      router.push("/payment/center/installment/payment-checkout");
    };

    return {
      amountPaymentChannelFormat,
      selectedInstallment,
      installmentOptions,
      payableAmount,
      selectedOption,
      submitCheckout,
    };
  },
};
</script>
